import { RevisionType } from '@/Components/RevisionHistoryModal/RevisionType';
import { sortRevisionListIntoGroups } from '@/Components/RevisionHistoryModal/utils';
import { STRING_RESTORE_LOCKED_ATTEMPT } from '@/Constants/Strings';
import { confirmDialog } from '@standardnotes/ui-services';
import { jtString } from '@standardnotes/features';
import { c } from 'ttag';
import { ActionVerb, ButtonType, PayloadEmitSource, } from '@standardnotes/snjs';
import { makeObservable, observable, action } from 'mobx';
import { RevisionContentState, } from './Types';
export class NoteHistoryController {
    constructor(note, itemListController, features, items, mutator, sync, actions, history, alerts, _getRevision, _listRevisions, _deleteRevision, _changeAndSaveItem) {
        this.note = note;
        this.itemListController = itemListController;
        this.features = features;
        this.items = items;
        this.mutator = mutator;
        this.sync = sync;
        this.actions = actions;
        this.history = history;
        this.alerts = alerts;
        this._getRevision = _getRevision;
        this._listRevisions = _listRevisions;
        this._deleteRevision = _deleteRevision;
        this._changeAndSaveItem = _changeAndSaveItem;
        this.remoteHistory = [];
        this.isFetchingRemoteHistory = false;
        this.sessionHistory = [];
        this.legacyHistory = [];
        this.selectedRevision = undefined;
        this.selectedEntry = undefined;
        this.contentState = RevisionContentState.Idle;
        this.currentTab = RevisionType.Remote;
        this.setSelectedRevision = (revision) => {
            this.selectedRevision = revision;
        };
        this.setSelectedEntry = (entry) => {
            this.selectedEntry = entry;
        };
        this.clearSelection = () => {
            this.setSelectedEntry(undefined);
            this.setSelectedRevision(undefined);
        };
        this.selectTab = (tab) => {
            this.currentTab = tab;
            this.clearSelection();
            this.setContentState(RevisionContentState.Idle);
            this.selectFirstRevision();
        };
        this.setIsFetchingRemoteHistory = (value) => {
            this.isFetchingRemoteHistory = value;
        };
        this.setContentState = (contentState) => {
            this.contentState = contentState;
        };
        this.selectRemoteRevision = async (entry) => {
            if (!this.note) {
                return;
            }
            if (!this.features.hasMinimumRole(entry.required_role)) {
                this.setContentState(RevisionContentState.NotEntitled);
                this.setSelectedRevision(undefined);
                return;
            }
            this.setContentState(RevisionContentState.Loading);
            this.clearSelection();
            try {
                this.setSelectedEntry(entry);
                const remoteRevisionOrError = await this._getRevision.execute({
                    itemUuid: this.note.uuid,
                    revisionUuid: entry.uuid,
                });
                if (remoteRevisionOrError.isFailed()) {
                    throw new Error(remoteRevisionOrError.getError());
                }
                const remoteRevision = remoteRevisionOrError.getValue();
                this.setSelectedRevision(remoteRevision);
            }
            catch (err) {
                this.clearSelection();
                console.error(err);
            }
            finally {
                this.setContentState(RevisionContentState.Loaded);
            }
        };
        this.selectLegacyRevision = async (entry) => {
            var _a;
            this.clearSelection();
            this.setContentState(RevisionContentState.Loading);
            if (!this.note) {
                return;
            }
            try {
                if (!((_a = entry.subactions) === null || _a === void 0 ? void 0 : _a[0])) {
                    throw new Error(c('B4.Notes.History.Error').t `Could not find revision action url`);
                }
                this.setSelectedEntry(entry);
                const response = await this.actions.runAction(entry.subactions[0], this.note);
                if (!response) {
                    throw new Error(c('B4.Notes.History.Error').t `Could not fetch revision`);
                }
                this.setSelectedRevision(response.item);
            }
            catch (error) {
                console.error(error);
                this.setSelectedRevision(undefined);
            }
            finally {
                this.setContentState(RevisionContentState.Loaded);
            }
        };
        this.selectSessionRevision = (entry) => {
            this.clearSelection();
            this.setSelectedEntry(entry);
            this.setSelectedRevision(entry);
            this.setContentState(RevisionContentState.Loaded);
        };
        this.selectFirstRevision = () => {
            switch (this.currentTab) {
                case RevisionType.Remote: {
                    const firstEntry = this.flattenedRemoteHistory[0];
                    if (firstEntry) {
                        void this.selectRemoteRevision(firstEntry);
                    }
                    break;
                }
                case RevisionType.Session: {
                    const firstEntry = this.flattenedSessionHistory[0];
                    if (firstEntry) {
                        void this.selectSessionRevision(firstEntry);
                    }
                    break;
                }
                case RevisionType.Legacy: {
                    const firstEntry = this.legacyHistory[0];
                    if (firstEntry) {
                        void this.selectLegacyRevision(firstEntry);
                    }
                    break;
                }
            }
        };
        this.selectPrevOrNextRemoteRevision = (revisionEntry) => {
            const currentIndex = this.flattenedRemoteHistory.findIndex((entry) => (entry === null || entry === void 0 ? void 0 : entry.uuid) === revisionEntry.uuid);
            const previousEntry = this.flattenedRemoteHistory[currentIndex - 1];
            const nextEntry = this.flattenedRemoteHistory[currentIndex + 1];
            if (previousEntry) {
                void this.selectRemoteRevision(previousEntry);
            }
            else if (nextEntry) {
                void this.selectRemoteRevision(nextEntry);
            }
        };
        this.setRemoteHistory = (remoteHistory) => {
            this.remoteHistory = remoteHistory;
        };
        this.fetchRemoteHistory = async () => {
            this.setRemoteHistory([]);
            if (this.note) {
                this.setIsFetchingRemoteHistory(true);
                try {
                    const revisionsListOrError = await this._listRevisions.execute({ itemUuid: this.note.uuid });
                    if (revisionsListOrError.isFailed()) {
                        throw new Error(revisionsListOrError.getError());
                    }
                    const revisionsList = revisionsListOrError.getValue();
                    this.setRemoteHistory(sortRevisionListIntoGroups(revisionsList));
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    this.setIsFetchingRemoteHistory(false);
                }
            }
        };
        this.setLegacyHistory = (legacyHistory) => {
            this.legacyHistory = legacyHistory;
        };
        this.fetchLegacyHistory = async () => {
            const actionExtensions = this.actions.getExtensions();
            actionExtensions.forEach(async (ext) => {
                if (!this.note) {
                    return;
                }
                const actionExtension = await this.actions.loadExtensionInContextOfItem(ext, this.note);
                if (!actionExtension) {
                    return;
                }
                const isLegacyNoteHistoryExt = actionExtension === null || actionExtension === void 0 ? void 0 : actionExtension.actions.some((action) => action.verb === ActionVerb.Nested);
                if (!isLegacyNoteHistoryExt) {
                    return;
                }
                this.setLegacyHistory(actionExtension.actions.filter((action) => { var _a; return (_a = action.subactions) === null || _a === void 0 ? void 0 : _a[0]; }));
            });
        };
        this.setSessionHistory = (sessionHistory) => {
            this.sessionHistory = sessionHistory;
        };
        this.fetchAllHistory = async () => {
            this.resetHistoryState();
            if (!this.note) {
                return;
            }
            this.setSessionHistory(sortRevisionListIntoGroups(this.history.sessionHistoryForItem(this.note)));
            await this.fetchRemoteHistory();
            await this.fetchLegacyHistory();
            this.selectFirstRevision();
        };
        this.resetHistoryState = () => {
            this.remoteHistory = [];
            this.sessionHistory = [];
            this.legacyHistory = [];
        };
        this.restoreRevision = async (revision) => {
            const originalNote = this.items.findItem(revision.payload.uuid);
            if (originalNote === null || originalNote === void 0 ? void 0 : originalNote.locked) {
                this.alerts.alert(STRING_RESTORE_LOCKED_ATTEMPT()).catch(console.error);
                return;
            }
            const didConfirm = await confirmDialog({
                text: c('B4.Notes.History.Label')
                    .t `Are you sure you want to replace the current note's contents with what you see in this preview?`,
                confirmButtonStyle: 'danger',
            });
            if (!originalNote) {
                throw new Error('Original note not found.');
            }
            if (didConfirm) {
                void this._changeAndSaveItem.execute(originalNote, (mutator) => {
                    mutator.setCustomContent(revision.payload.content);
                }, true, PayloadEmitSource.RemoteRetrieved);
            }
        };
        this.restoreRevisionAsCopy = async (revision) => {
            const originalNote = this.items.findSureItem(revision.payload.uuid);
            const duplicatedItem = await this.mutator.duplicateItem(originalNote, false, {
                ...revision.payload.content,
                title: revision.payload.content.title
                    ? jtString(c('B4.Notes.History.Label').jt `${revision.payload.content.title} (copy)`)
                    : undefined,
            });
            void this.sync.sync();
            this.itemListController.selectItem(duplicatedItem.uuid).catch(console.error);
        };
        this.deleteRemoteRevision = async (revisionEntry) => {
            const shouldDelete = await this.alerts.confirm(c('B4.Notes.History.Label').t `Are you sure you want to delete this revision?`, c('B4.Notes.History.Label').t `Delete revision?`, c('B4.Notes.History.Action').t `Delete revision`, ButtonType.Danger, c('B4.Notes.History.Action').t `Cancel`);
            if (!shouldDelete || !this.note) {
                return;
            }
            const deleteRevisionOrError = await this._deleteRevision.execute({
                itemUuid: this.note.uuid,
                revisionUuid: revisionEntry.uuid,
            });
            if (deleteRevisionOrError.isFailed()) {
                throw new Error(deleteRevisionOrError.getError());
            }
            this.clearSelection();
            this.selectPrevOrNextRemoteRevision(revisionEntry);
            await this.fetchRemoteHistory();
        };
        makeObservable(this, {
            selectedRevision: observable,
            setSelectedRevision: action,
            selectedEntry: observable,
            setSelectedEntry: action,
            remoteHistory: observable,
            setRemoteHistory: action,
            isFetchingRemoteHistory: observable,
            setIsFetchingRemoteHistory: action,
            sessionHistory: observable,
            setSessionHistory: action,
            legacyHistory: observable,
            setLegacyHistory: action,
            resetHistoryState: action,
            currentTab: observable,
            selectTab: action,
            contentState: observable,
            setContentState: action,
        });
        void this.fetchAllHistory();
    }
    get flattenedRemoteHistory() {
        return this.remoteHistory.map((group) => group.entries).flat();
    }
    get flattenedSessionHistory() {
        return this.sessionHistory.map((group) => group.entries).flat();
    }
}
